import { useEffect, useState } from "react";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { r2, today } from "@/lib/format";
import { Field, Money, NativeSelect, PageHeader, Panel } from "@/components/common";
import { ExportButtons } from "@/components/ExportButtons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const LINE = { account_id: "", debit: "", credit: "" };

function EntryDialog({ open, onClose, accounts, onSaved }) {
  const { t, accName } = useLang();
  const [f, setF] = useState({ date: today(), reference: "", description: "" });
  const [lines, setLines] = useState([{ ...LINE }, { ...LINE }]);
  useEffect(() => { if (open) { setF({ date: today(), reference: "", description: "" }); setLines([{ ...LINE }, { ...LINE }]); } }, [open]);
  const td = r2(lines.reduce((s, l) => s + (Number(l.debit) || 0), 0));
  const tc = r2(lines.reduce((s, l) => s + (Number(l.credit) || 0), 0));
  const setLine = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const submit = async (e) => {
    e.preventDefault();
    if (td !== tc || td === 0) return toast.error(t("unbalanced"));
    try {
      await api.post("/journal", { ...f, lines: lines.filter((l) => l.account_id).map((l) => ({ account_id: l.account_id, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0 })) });
      toast.success(t("saved"));
      onSaved();
    } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="journal-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle className="font-display">{t("newEntry")}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Field label={t("date")}><Input data-testid="journal-date-input" type="date" className="font-mono" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} required /></Field>
            <Field label={t("reference")}><Input data-testid="journal-ref-input" value={f.reference} onChange={(e) => setF({ ...f, reference: e.target.value })} /></Field>
            <Field label={t("description")}><Input data-testid="journal-desc-input" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} /></Field>
          </div>
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 gap-2" data-testid={`journal-line-${i}`}>
              <NativeSelect data-testid={`journal-line-account-${i}`} className="col-span-6" value={l.account_id} onChange={(e) => setLine(i, "account_id", e.target.value)}>
                <option value="">{t("selectAccount")}</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} · {accName(a)}</option>)}
              </NativeSelect>
              <Input data-testid={`journal-line-debit-${i}`} placeholder={t("debit")} type="number" min="0" step="0.01" className="col-span-3 font-mono" value={l.debit} onChange={(e) => setLine(i, "debit", e.target.value)} />
              <Input data-testid={`journal-line-credit-${i}`} placeholder={t("credit")} type="number" min="0" step="0.01" className="col-span-3 font-mono" value={l.credit} onChange={(e) => setLine(i, "credit", e.target.value)} />
            </div>
          ))}
          <div className="flex items-center justify-between">
            <Button type="button" variant="outline" data-testid="journal-add-line-btn" onClick={() => setLines([...lines, { ...LINE }])}><Plus className="me-1 h-4 w-4" />{t("addLine")}</Button>
            <div className={`text-sm ${td === tc && td > 0 ? "text-[#007A3D]" : "text-[#CE1126]"}`} data-testid="journal-balance-indicator">
              {t("totalDebit")} <Money value={td} cur={false} /> · {t("totalCredit")} <Money value={tc} cur={false} />
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={onClose}>{t("cancel")}</Button>
            <Button data-testid="journal-save-btn" className="bg-[#007A3D] hover:bg-[#005E2E]">{t("save")}</Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Journal() {
  const { t, accName } = useLang();
  const [rows, setRows] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [open, setOpen] = useState(false);
  const load = () => api.get("/journal").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/accounts").then((r) => setAccounts(r.data)); }, []);
  const byId = Object.fromEntries(accounts.map((a) => [a.id, a]));
  const remove = async (e) => {
    if (!window.confirm(t("confirmDelete"))) return;
    try { await api.delete(`/journal/${e.id}`); toast.success(t("deleted")); load(); } catch (err) { toast.error(errMsg(err)); }
  };
  return (
    <div data-testid="journal-page">
      <PageHeader title={t("journal")} subtitle={t("journalSubtitle")}
        actions={<div className="flex flex-wrap items-center gap-2"><ExportButtons path="/export/journal" testid="journal-export" /><Button data-testid="new-entry-btn" onClick={() => setOpen(true)} className="bg-[#007A3D] hover:bg-[#005E2E]"><Plus className="me-1 h-4 w-4" />{t("newEntry")}</Button></div>} />
      <div className="space-y-4">
        {rows.length === 0 && <Panel className="p-10 text-center text-sm text-slate-400">{t("noData")}</Panel>}
        {rows.map((e) => (
          <Panel key={e.id} className="overflow-hidden" data-testid={`journal-entry-${e.id}`}>
            <div className="flex flex-wrap items-center gap-3 border-b border-slate-100 bg-slate-50/60 px-5 py-2.5 text-sm">
              <span className="font-mono text-slate-500">{e.date}</span>
              <span className="font-mono font-semibold text-slate-900">{e.reference}</span>
              <span className="text-slate-600">{e.description}</span>
              <span className="ms-auto rounded-full bg-white px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wider text-slate-500 ring-1 ring-slate-200">{t(`src_${e.source}`)}</span>
              {e.source === "manual" && <button data-testid={`delete-entry-${e.id}`} onClick={() => remove(e)} className="rounded p-1 text-slate-400 hover:text-[#CE1126]"><Trash2 className="h-4 w-4" /></button>}
            </div>
            <table className="data-table compact">
              <tbody>
                {e.lines.map((l, i) => (
                  <tr key={i}>
                    <td className={`w-1/2 ${l.credit ? "ps-10" : ""}`}><span className="font-mono text-slate-400">{byId[l.account_id]?.code}</span> {accName(byId[l.account_id])}</td>
                    <td className="text-end">{l.debit ? <Money value={l.debit} cur={false} /> : ""}</td>
                    <td className="text-end">{l.credit ? <Money value={l.credit} cur={false} /> : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Panel>
        ))}
      </div>
      <EntryDialog open={open} accounts={accounts} onClose={() => setOpen(false)} onSaved={() => { setOpen(false); load(); }} />
    </div>
  );
}
